import { Suspense } from "react";
import { Loader2 } from "lucide-react";
import { Routes, Route, Navigate } from "react-router-dom";
import { GuardedBrowserRouter } from "@/lib/navigation/NavigationGuardProvider";
import { LegacyBuildingRedirect } from "./components/routing/LegacyBuildingRedirect";
import { LegacyFoldersRedirect } from "./components/routing/LegacyFoldersRedirect";
import { LegacyDocumentsLogRedirect } from "./components/routing/LegacyDocumentsLogRedirect";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";

// Import context providers
import { LanguageProvider } from "@/lib/i18n/LanguageContext";
import { AuthProvider } from "@/hooks/AuthProvider";
import { StoreProvider } from "@/stores";
import { createStore } from "@/stores";
import { OrganizationProvider } from "@/lib/context/OrganizationContext";
import { BillingStatusProvider } from "@/domains/billing/BillingStatusProvider";
import { BuildingBillingScopeProvider } from "@/domains/billing/BuildingBillingScopeProvider";
import { BillingRestrictionNotice } from "@/domains/billing/components/BillingRestrictionNotice";
import { ContextErrorBoundary } from "@/components/error/ContextErrorBoundary";
import { PostHogProvider } from "@/lib/posthog";
import { PostHogIdentity } from "@/lib/posthog/PostHogIdentity";
import { PMModuleGate } from "@/components/permissions/PMModuleGate";
import { lazyWithReload } from "@/utils/lazyWithReload";

// Import pages
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Help from "./pages/Help";
import Buildings from "./pages/Buildings";
import BuildingDetail from "./pages/BuildingDetail";
import EditBuilding from "./pages/EditBuilding";
import BuildingDataPage from "./pages/BuildingDataPage";
import BuildingDocuments from "./pages/BuildingDocuments";
import ProjectsDashboard from "./pages/ProjectsDashboard";
// Lazy-loaded so the three.js / @thatopen 3D stack ships in its own chunk
// (see vite.config.ts manualChunks "ifc-viewer") and stays out of the entry
// bundle every visitor downloads. Only users who open the Model tab pay for
// it. Named export → map to default for React.lazy. (WHA-3689)
// lazyWithReload: a stale tab after a redeploy requests an old chunk hash that
// 404s to the SPA fallback; this reloads once onto the current build.
const IfcViewerPage = lazyWithReload(() =>
  import("./domains/buildings/components/ifc-viewer/IfcViewerPage").then((m) => ({
    default: m.IfcViewerPage,
  }))
);
// Drawing Compare (Canvas2D + diff Web Worker) is likewise lazy so the DWG
// compare stack stays out of the entry bundle — shared by the building-scoped
// Models route, the /tools hub, and the /tools/drawings URL-ids entry.
const DrawingComparePage = lazyWithReload(() =>
  import("./domains/drawing-compare/DrawingComparePage").then((m) => ({
    default: m.DrawingComparePage,
  }))
);
const Tools = lazyWithReload(() => import("./pages/tools/Tools"));
const ContactsPage = lazyWithReload(() => import("./pages/Contacts"));

// The documents-table View/Compare entry — the same DrawingCompare tool,
// seeded from ?ids=.
const DrawingViewerPage = lazyWithReload(() =>
  import("./domains/drawing-compare/DrawingViewerPage").then((m) => ({
    default: m.DrawingViewerPage,
  }))
);
import Signing from "./pages/Signing";
const BriefPage = lazyWithReload(() => import("./pages/Brief"));
import PMIssues from "@/pages/pm/PMIssues";
import PMMyIssues from "@/pages/pm/PMMyIssues";
import PMNotifications from "@/pages/pm/PMNotifications";
import { ProjectDetail } from "./pages/ProjectDetail";
import { ProjectDocumentsLog } from "./pages/ProjectDocumentsLog";
import Dashboard from "./pages/admin/Dashboard";
import Users from "./pages/admin/Users";
import DataManagement from "./pages/admin/DataManagement";
import RolesAdminPage from "./pages/admin/Roles";
import SharePage from "./pages/SharePage";
import InviteAccept from "./pages/InviteAccept";
import AuthConfirm from "./pages/AuthConfirm";
import AuthCallback from "./pages/AuthCallback";
import NotFound from "./pages/NotFound";
import Settings from "./pages/Settings";
import ConnectorAuthorize from "./pages/ConnectorAuthorize";
import ConnectorAction from "./pages/ConnectorAction";
import ConnectorUpload from "./pages/ConnectorUpload";
import OrganizationSettings from "./pages/OrganizationSettings";
import CreateOrganization from "./pages/CreateOrganization";

// Import layout components
import { BuildingLayout } from "./components/layout/BuildingLayout";

// Import components
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";
import { ProjectPermissionRouteGuard } from "@/components/permissions/ProjectPermissionRouteGuard";
import PermissionProtectedRoute from "./components/auth/PermissionProtectedRoute";
import { TosConsentGate } from "@/components/auth/TosConsentGate";
import { PermissionGuard } from "@/components/permissions/PermissionGuard";
import { SubcontractorRedirect } from "@/components/permissions/SubcontractorRedirect";
import { PERMISSIONS } from "@/constants/permissions";
import { EnvironmentSwitcher } from "@/components/dev/EnvironmentSwitcher";
import { OrganizeWatcher } from "@/domains/documents/components/organize/OrganizeWatcher";
import { IfcModelOverlayProvider } from "@/domains/buildings/components/ifc-viewer/IfcModelOverlay";
import { MobileBlockingScreen } from "@/components/MobileBlockingScreen";
import { queryClient } from "@/lib/queryClient";
import { debugLog } from "./utils/debugLog";

// Create the MST store once at module scope
const store = createStore();

function App() {
  debugLog("🏗️ App component rendering");

  return (
    <ContextErrorBoundary>
      <PostHogProvider>
        <LanguageProvider>
          <StoreProvider store={store}>
            <AuthProvider>
              <QueryClientProvider client={queryClient}>
                <OrganizationProvider>
                  <BillingStatusProvider>
                    <TooltipProvider>
                      {/* Below both providers it reads: the auth user and the selected org. */}
                      <PostHogIdentity />
                      <SonnerToaster />
                      <IfcModelOverlayProvider>
                        <GuardedBrowserRouter>
                          <MobileBlockingScreen />
                          {/* Inside the Router: EnvironmentSwitcher calls useLocation
                            (it hides itself on the tool screens). */}
                          <EnvironmentSwitcher />
                          {/* Also inside the Router, and above <Routes> so it still
                            survives navigation: its ready-toast offers a link to
                            the folder whose proposal is waiting, which needs
                            useNavigate. */}
                          <OrganizeWatcher />
                          {/* Read-only / status-unknown switch off every mutating
                            control in the app, so the reason has to be reachable
                            from every page, not only from /billing. */}
                          <BuildingBillingScopeProvider>
                            <BillingRestrictionNotice />
                            <Routes>
                              <Route
                                path="/"
                                element={
                                  <ProtectedRoute>
                                    <Navigate to="/buildings" replace />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Public routes */}
                              <Route path="/login" element={<Login />} />
                              <Route path="/signup" element={<SignUp />} />
                              <Route path="/forgot-password" element={<ForgotPassword />} />
                              <Route path="/reset-password" element={<ResetPassword />} />
                              <Route path="/help" element={<Help />} />
                              {/* Deliberately NOT gated: a share link is a public,
                              unauthenticated, read-only view of someone else's
                              documents — it joins nothing and changes no state, and
                              the same URL opens in a signed-out browser. Gating it
                              would only block a signed-in recipient from a page
                              anyone else can already open. */}
                              <Route path="/share/:token" element={<SharePage />} />
                              {/* Accepting an invite joins an organisation, so it is
                              gated for the same reason /create-organization is:
                              an authenticated account with `tos_accepted_at` NULL
                              must not become a member without consenting. The gate
                              is inert without a userId, so the unauthenticated
                              visitor still reaches the page's own sign-up path. */}
                              <Route
                                path="/invite/accept"
                                element={
                                  <TosConsentGate>
                                    <InviteAccept />
                                  </TosConsentGate>
                                }
                              />
                              <Route path="/auth/confirm" element={<AuthConfirm />} />
                              <Route path="/auth/callback" element={<AuthCallback />} />
                              <Route
                                path="/connector/authorize"
                                element={
                                  <TosConsentGate>
                                    <ConnectorAuthorize />
                                  </TosConsentGate>
                                }
                              />
                              <Route
                                path="/connector/uploads/:id"
                                element={
                                  <TosConsentGate>
                                    <ConnectorUpload />
                                  </TosConsentGate>
                                }
                              />
                              <Route
                                path="/connector/actions/:id"
                                element={
                                  <TosConsentGate>
                                    <ConnectorAction />
                                  </TosConsentGate>
                                }
                              />

                              {/* Special route - requires auth but not org membership.
                              No ProtectedRoute (it would bounce a user who has no
                              org yet), so the consent gate is mounted directly —
                              otherwise a brand-new account could create an
                              organisation without ever being asked to accept. The
                              gate is inert without a userId, so an unauthenticated
                              visitor still just sees the page's own redirect. */}
                              <Route
                                path="/create-organization"
                                element={
                                  <TosConsentGate>
                                    <CreateOrganization />
                                  </TosConsentGate>
                                }
                              />

                              {/* Protected routes */}
                              <Route
                                path="/buildings"
                                element={
                                  <ProtectedRoute>
                                    <Buildings />
                                  </ProtectedRoute>
                                }
                              />
                              {/* Legacy route: "Add New Building" is now a modal
                              on the buildings list (not a full page). Redirect
                              deep links; the list opens the modal via ?new=1. */}
                              <Route
                                path="/buildings/new"
                                element={<Navigate to="/buildings?new=1" replace />}
                              />
                              {/* Building pages with nested routing under BuildingLayout */}
                              <Route
                                path="/buildings/:id"
                                element={
                                  <ProtectedRoute>
                                    <LegacyBuildingRedirect>
                                      <BuildingLayout />
                                    </LegacyBuildingRedirect>
                                  </ProtectedRoute>
                                }
                              >
                                <Route
                                  index
                                  element={
                                    <SubcontractorRedirect>
                                      <BuildingDetail />
                                    </SubcontractorRedirect>
                                  }
                                />
                                <Route
                                  path="edit"
                                  element={
                                    <PermissionGuard permission={PERMISSIONS.MODIFY_DATA}>
                                      <EditBuilding />
                                    </PermissionGuard>
                                  }
                                />
                                {/* Building-view attribute browser — permission
                                gating (VIEW/MANAGE_BUILDING_DATA_SIDEBAR,
                                incl. platform-admin bypass) lives inside the
                                page, not a PermissionGuard. Building view only,
                                which a subcontractor-only member never has. */}
                                <Route
                                  path="data"
                                  element={
                                    <SubcontractorRedirect>
                                      <BuildingDataPage />
                                    </SubcontractorRedirect>
                                  }
                                />
                                {/* Projects-view dashboard — scoped to the
                                header-selected project. Open to members too:
                                it is their project's landing page, and every
                                widget on it is RLS-scoped or permission-gated
                                (the building dashboard and Building Data stay
                                behind SubcontractorRedirect). */}
                                <Route path="project-dashboard" element={<ProjectsDashboard />} />
                                <Route path="documents" element={<BuildingDocuments />} />
                                {/* Digital signing — the building's signed .asice
                                containers and the user's own open signing
                                sessions. The page redirects without
                                useSigningVisible (the same gate as the sidebar
                                tab: the org's signing_enabled flag) UNLESS the
                                viewer has rows here, so switching an org off
                                can't strand a chain already in flight; rows are
                                RLS-scoped either way. */}
                                <Route path="signing" element={<Signing />} />
                                {/* Project brief (lähteülesanne). VIEW_BRIEFS is the
                                same gate as the sidebar tab; the guard waits for
                                the org context so a cold deep link never
                                bounces a user who does have access. */}
                                <Route
                                  path="brief"
                                  element={
                                    <PermissionProtectedRoute permission={PERMISSIONS.VIEW_BRIEFS}>
                                      <Suspense
                                        fallback={
                                          <div className="flex h-full w-full items-center justify-center">
                                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                          </div>
                                        }
                                      >
                                        <BriefPage />
                                      </Suspense>
                                    </PermissionProtectedRoute>
                                  }
                                />
                                {/* Contacts — the project phone book. Same gate as the
                                sidebar tab; the page itself narrows to the
                                building/project assignment. */}
                                <Route
                                  path="contacts"
                                  element={
                                    <PermissionProtectedRoute
                                      permission={PERMISSIONS.VIEW_CONTACTS}
                                    >
                                      <ProjectPermissionRouteGuard feature="contacts">
                                        <Suspense
                                          fallback={
                                            <div className="flex h-full w-full items-center justify-center">
                                              <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                            </div>
                                          }
                                        >
                                          <ContactsPage />
                                        </Suspense>
                                      </ProjectPermissionRouteGuard>
                                    </PermissionProtectedRoute>
                                  }
                                />
                                {/* Removed page — old links land on the Timeline. */}
                                <Route
                                  path="documents-log"
                                  element={<LegacyDocumentsLogRedirect />}
                                />
                                {/* Folder browsing lives in the Files page's Folders
                                tab now — both legacy folder routes forward there. */}
                                <Route path="folders" element={<LegacyFoldersRedirect />} />
                                {/* Renamed tab — old links keep working. */}
                                <Route path="projects" element={<LegacyFoldersRedirect />} />
                                {/* The 3D viewer is open to all org members; the
                                IFC documents shown inside are RLS-scoped (a
                                subcontractor only sees their projects' files). */}
                                <Route
                                  path="model"
                                  element={
                                    <ProjectPermissionRouteGuard feature="modelViewer">
                                      <Suspense
                                        fallback={
                                          <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                                            Loading 3D viewer…
                                          </div>
                                        }
                                      >
                                        <IfcViewerPage />
                                      </Suspense>
                                    </ProjectPermissionRouteGuard>
                                  }
                                />
                                {/* Drawing Compare, building-scoped — sits next
                                to the 3D model under the Models sidebar
                                section. This is the customer entry point:
                                gated on VIEW_DRAWING_COMPARE_TOOL, granted to
                                all three org roles. Per-project access is
                                enforced server-side by
                                request_drawing_geometry. */}
                                <Route
                                  path="drawings"
                                  element={
                                    <PermissionProtectedRoute
                                      permission={PERMISSIONS.VIEW_DRAWING_COMPARE_TOOL}
                                    >
                                      <ProjectPermissionRouteGuard feature="drawingCompare">
                                        <Suspense
                                          fallback={
                                            <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                                              Loading…
                                            </div>
                                          }
                                        >
                                          <DrawingComparePage />
                                        </Suspense>
                                      </ProjectPermissionRouteGuard>
                                    </PermissionProtectedRoute>
                                  }
                                />
                                <Route
                                  path="issues"
                                  element={
                                    <PMModuleGate>
                                      <PermissionGuard permission={PERMISSIONS.VIEW_PM_TOOL}>
                                        <ProjectPermissionRouteGuard feature="issues">
                                          <PMIssues />
                                        </ProjectPermissionRouteGuard>
                                      </PermissionGuard>
                                    </PMModuleGate>
                                  }
                                />
                                <Route
                                  path="my-issues"
                                  element={
                                    <PMModuleGate>
                                      <PermissionGuard permission={PERMISSIONS.VIEW_PM_TOOL}>
                                        <ProjectPermissionRouteGuard feature="issues">
                                          <PMMyIssues />
                                        </ProjectPermissionRouteGuard>
                                      </PermissionGuard>
                                    </PMModuleGate>
                                  }
                                />
                                <Route
                                  path="notifications"
                                  element={
                                    <PMModuleGate>
                                      <PermissionGuard permission={PERMISSIONS.VIEW_PM_TOOL}>
                                        <ProjectPermissionRouteGuard feature="issues">
                                          <PMNotifications />
                                        </ProjectPermissionRouteGuard>
                                      </PermissionGuard>
                                    </PMModuleGate>
                                  }
                                />
                              </Route>
                              {/* Canonical project route — used by subcontractors as their primary
                          workspace and linkable from anywhere in the app for any user who
                          has PROJECT_READ on the given project. */}
                              <Route
                                path="/projects/:id"
                                element={
                                  <ProtectedRoute>
                                    <ProjectDetail />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/projects/:id/log"
                                element={
                                  <ProtectedRoute>
                                    <ProjectDocumentsLog />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/settings"
                                element={
                                  <ProtectedRoute requireOrganization={false}>
                                    <Settings />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/settings/help"
                                element={
                                  <ProtectedRoute requireOrganization={false}>
                                    <Settings />
                                  </ProtectedRoute>
                                }
                              />
                              {/* Organisation settings and billing are one page
                              with a route-driven section. PermissionProtectedRoute,
                              not PermissionGuard: the guard renders its fallback the
                              instant hasPermission is false, and usePermissions is
                              all-false until the org context resolves — so a cold
                              load of a deep link bounced a legitimate admin. */}
                              <Route
                                path="/organization-settings"
                                element={
                                  <ProtectedRoute>
                                    <PermissionProtectedRoute
                                      permission={PERMISSIONS.MANAGE_ORG_PERMISSIONS}
                                    >
                                      <OrganizationSettings />
                                    </PermissionProtectedRoute>
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/organization-settings/users"
                                element={
                                  <ProtectedRoute>
                                    <PermissionProtectedRoute
                                      permission={PERMISSIONS.MANAGE_ORG_PERMISSIONS}
                                    >
                                      <OrganizationSettings />
                                    </PermissionProtectedRoute>
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/billing"
                                element={
                                  <ProtectedRoute>
                                    <PermissionProtectedRoute
                                      permission={PERMISSIONS.MANAGE_ORG_PERMISSIONS}
                                    >
                                      <OrganizationSettings />
                                    </PermissionProtectedRoute>
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/billing/details"
                                element={
                                  <ProtectedRoute>
                                    <PermissionProtectedRoute
                                      permission={PERMISSIONS.MANAGE_ORG_PERMISSIONS}
                                    >
                                      <OrganizationSettings />
                                    </PermissionProtectedRoute>
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="/billing/invoices"
                                element={
                                  <ProtectedRoute>
                                    <PermissionProtectedRoute
                                      permission={PERMISSIONS.MANAGE_ORG_PERMISSIONS}
                                    >
                                      <OrganizationSettings />
                                    </PermissionProtectedRoute>
                                  </ProtectedRoute>
                                }
                              />
                              {/* Tools — 2D DWG drawing viewer/compare via
                              ?ids=<docId>[,<docId2>] (building context comes
                              from the documents themselves, RLS-scoped).
                              Kept for old deep links — the documents-table
                              actions now route to the building-scoped
                              /buildings/:id/drawings. Staff-only like every
                              other route to this tool. */}
                              <Route
                                path="/tools/drawings"
                                element={
                                  <PermissionProtectedRoute
                                    permission={PERMISSIONS.VIEW_DRAWING_COMPARE_TOOL}
                                  >
                                    <Suspense
                                      fallback={
                                        <div className="flex h-screen w-full items-center justify-center text-sm text-muted-foreground">
                                          Loading drawing viewer…
                                        </div>
                                      }
                                    >
                                      <DrawingViewerPage />
                                    </Suspense>
                                  </PermissionProtectedRoute>
                                }
                              />
                              {/* Admin routes */}
                              <Route
                                path="/admin/dashboard"
                                element={
                                  <AdminProtectedRoute>
                                    <Dashboard />
                                  </AdminProtectedRoute>
                                }
                              />
                              <Route
                                path="/admin/users"
                                element={
                                  <AdminProtectedRoute>
                                    <Users />
                                  </AdminProtectedRoute>
                                }
                              />
                              <Route
                                path="/admin/data-management"
                                element={
                                  <AdminProtectedRoute>
                                    <DataManagement />
                                  </AdminProtectedRoute>
                                }
                              />
                              <Route
                                path="/admin/roles"
                                element={
                                  <AdminProtectedRoute>
                                    <RolesAdminPage />
                                  </AdminProtectedRoute>
                                }
                              />

                              <Route
                                path="/tools"
                                element={
                                  <AdminProtectedRoute>
                                    <Suspense
                                      fallback={
                                        <div className="flex h-screen w-full items-center justify-center text-sm text-muted-foreground">
                                          Loading…
                                        </div>
                                      }
                                    >
                                      <Tools />
                                    </Suspense>
                                  </AdminProtectedRoute>
                                }
                              />

                              {/* Catch all route */}
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </BuildingBillingScopeProvider>
                        </GuardedBrowserRouter>
                      </IfcModelOverlayProvider>
                    </TooltipProvider>
                  </BillingStatusProvider>
                </OrganizationProvider>
              </QueryClientProvider>
            </AuthProvider>
          </StoreProvider>
        </LanguageProvider>
      </PostHogProvider>
    </ContextErrorBoundary>
  );
}

export default App;
